/**
 * Global keyboard handling.
 *
 * The layout is FastTracker II's, because that is the muscle memory anyone who
 * used a tracker in the nineties still has: two rows of piano keys, hex entry in
 * the numeric columns, and the same keys meaning different things depending on
 * which column the cursor is in.
 */
import { useEffect, useRef } from 'react';
import { useTracker, COL_NOTE, COL_INSTRUMENT_HI, COL_INSTRUMENT_LO, COL_VOLUME_HI, COL_VOLUME_LO, COL_EFFECT, COL_PARAM_HI, COL_PARAM_LO } from '../../state/store';
import { CELL_EFFECT, CELL_INSTRUMENT, CELL_PARAM, CELL_VOLUME, NOTE_KEYOFF } from '../../engine/types';
import { audioEngine } from '../../engine/audio';

/**
 * Semitone offsets from the current octave.
 *
 * The lower row starts at the selected octave; the upper row sits an octave
 * above it, so two octaves are always under the hands.
 */
const LOWER_ROW: Record<string, number> = {
  KeyZ: 0, KeyS: 1, KeyX: 2, KeyD: 3, KeyC: 4, KeyV: 5, KeyG: 6, KeyB: 7,
  KeyH: 8, KeyN: 9, KeyJ: 10, KeyM: 11, Comma: 12, KeyL: 13, Period: 14,
  Semicolon: 15, Slash: 16,
};

const UPPER_ROW: Record<string, number> = {
  KeyQ: 12, Digit2: 13, KeyW: 14, Digit3: 15, KeyE: 16, KeyR: 17, Digit5: 18,
  KeyT: 19, Digit6: 20, KeyY: 21, Digit7: 22, KeyU: 23, KeyI: 24, Digit9: 25,
  KeyO: 26, Digit0: 27, KeyP: 28,
};

const HEX_KEYS: Record<string, number> = {
  Digit0: 0, Digit1: 1, Digit2: 2, Digit3: 3, Digit4: 4, Digit5: 5, Digit6: 6,
  Digit7: 7, Digit8: 8, Digit9: 9, KeyA: 10, KeyB: 11, KeyC: 12, KeyD: 13,
  KeyE: 14, KeyF: 15,
};

/** Effect letters map to the numbers XM stores: 0-9 then A-Z as 10-35. */
function effectFromKey(code: string): number | null {
  if (code.startsWith('Digit')) return Number(code.slice(5));
  if (code.startsWith('Key')) {
    const letter = code.slice(3);
    if (letter.length === 1 && letter >= 'A' && letter <= 'Z') {
      return letter.charCodeAt(0) - 55;
    }
  }
  return null;
}

const HEX_COLUMNS = new Set([
  COL_INSTRUMENT_HI, COL_INSTRUMENT_LO, COL_VOLUME_HI, COL_VOLUME_LO,
  COL_PARAM_HI, COL_PARAM_LO,
]);

export function useTrackerKeyboard(enabled = true) {
  // Which preview voice each physical key is using, so key-up releases the
  // right note when chords are held.
  const heldKeys = useRef(new Map<string, number>());
  const nextVoice = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      // Never steal keys from a text field.
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      const store = useTracker.getState();
      const {
        song, cursorRow, cursorChannel, cursorColumn, editMode, octave, editStep,
        currentInstrument, playback, followPlayback,
      } = store;

      const ctrl = event.ctrlKey || event.metaKey;
      const shift = event.shiftKey;
      const alt = event.altKey;
      const pattern = store.currentPattern();

      // Where an edit lands: at the playing row when recording live, else at
      // the cursor.
      const targetRow = editMode && playback.playing && followPlayback ? playback.row : cursorRow;

      const advance = () => {
        if (editStep > 0 && !(playback.playing && followPlayback)) {
          store.setCursor((cursorRow + editStep) % pattern.rows, cursorChannel, cursorColumn);
        }
      };

      // ---- Transport and global commands --------------------------------
      if (ctrl) {
        switch (event.code) {
          case 'KeyZ':
            event.preventDefault();
            shift ? store.redo() : store.undo();
            return;
          case 'KeyY':
            event.preventDefault();
            store.redo();
            return;
          case 'KeyC':
            event.preventDefault();
            store.copySelection();
            return;
          case 'KeyX':
            event.preventDefault();
            store.cutSelection();
            return;
          case 'KeyV':
            event.preventDefault();
            store.pasteClipboard();
            return;
          case 'KeyA':
            event.preventDefault();
            store.selectAll();
            return;
          case 'ArrowUp':
            event.preventDefault();
            store.setOctave(octave + 1);
            return;
          case 'ArrowDown':
            event.preventDefault();
            store.setOctave(octave - 1);
            return;
          default:
            break;
        }
      }

      switch (event.code) {
        case 'Space':
          event.preventDefault();
          if (shift) {
            playback.playing ? audioEngine.pause() : audioEngine.play();
          } else {
            store.setEditMode(!editMode);
          }
          return;

        case 'Enter':
        case 'NumpadEnter':
          event.preventDefault();
          audioEngine.seek(store.currentOrder, shift ? 0 : cursorRow);
          audioEngine.play();
          return;

        case 'Escape':
          event.preventDefault();
          audioEngine.stop();
          audioEngine.allNotesOff();
          store.setEditMode(false);
          store.setSelection(null);
          return;

        case 'ArrowUp':
          event.preventDefault();
          if (shift) store.extendSelection(Math.max(0, cursorRow - 1), cursorChannel);
          else store.setSelection(null);
          store.moveCursor(-1, 0);
          return;

        case 'ArrowDown':
          event.preventDefault();
          if (shift) store.extendSelection(Math.min(pattern.rows - 1, cursorRow + 1), cursorChannel);
          else store.setSelection(null);
          store.moveCursor(1, 0);
          return;

        case 'ArrowLeft':
          event.preventDefault();
          store.moveCursor(0, -1);
          return;

        case 'ArrowRight':
          event.preventDefault();
          store.moveCursor(0, 1);
          return;

        case 'Tab':
          event.preventDefault();
          store.setCursor(cursorRow, cursorChannel + (shift ? -1 : 1), COL_NOTE);
          return;

        case 'PageUp':
          event.preventDefault();
          store.setCursor(Math.max(0, cursorRow - 16), cursorChannel, cursorColumn);
          return;

        case 'PageDown':
          event.preventDefault();
          store.setCursor(Math.min(pattern.rows - 1, cursorRow + 16), cursorChannel, cursorColumn);
          return;

        case 'Home':
          event.preventDefault();
          store.setCursor(0, cursorChannel, cursorColumn);
          return;

        case 'End':
          event.preventDefault();
          store.setCursor(pattern.rows - 1, cursorChannel, cursorColumn);
          return;

        case 'BracketLeft':
          event.preventDefault();
          store.setOctave(octave - 1);
          return;

        case 'BracketRight':
          event.preventDefault();
          store.setOctave(octave + 1);
          return;

        case 'Delete':
          event.preventDefault();
          if (store.selection) store.clearSelection();
          else {
            store.clearCell(targetRow, cursorChannel, cursorColumn);
            advance();
          }
          return;

        case 'Insert':
          event.preventDefault();
          store.insertRow();
          return;

        case 'Backspace':
          event.preventDefault();
          store.deleteRow();
          return;

        case 'F1':
        case 'F2':
        case 'F3':
        case 'F4':
        case 'F5':
        case 'F6':
        case 'F7':
        case 'F8': {
          // F1..F8 select an octave, as they do in ProTracker's clones.
          event.preventDefault();
          store.setOctave(Number(event.code.slice(1)) - 1);
          return;
        }

        case 'Backquote': {
          // Key-off, the note that releases an envelope.
          event.preventDefault();
          if (editMode && cursorColumn === COL_NOTE) {
            store.writeNote(targetRow, cursorChannel, NOTE_KEYOFF, 0);
            advance();
          }
          return;
        }

        default:
          break;
      }

      if (ctrl || alt) return;

      // ---- Note entry and preview ----------------------------------------
      if (cursorColumn === COL_NOTE) {
        const semitone = LOWER_ROW[event.code] ?? UPPER_ROW[event.code];
        if (semitone !== undefined) {
          event.preventDefault();
          if (event.repeat) return;

          const note = octave * 12 + semitone + 1; // stored 1-based
          if (note < 1 || note > 96) return;

          const voice = nextVoice.current++ % 8;
          heldKeys.current.set(event.code, voice);
          audioEngine.noteOn(voice, note, currentInstrument);

          if (editMode) {
            store.writeNote(targetRow, cursorChannel, note, currentInstrument);
            advance();
          }
          return;
        }
      }

      if (!editMode) return;

      // ---- Hex entry ------------------------------------------------------
      if (HEX_COLUMNS.has(cursorColumn)) {
        const digit = HEX_KEYS[event.code];
        if (digit === undefined) return;
        event.preventDefault();

        const field =
          cursorColumn === COL_INSTRUMENT_HI || cursorColumn === COL_INSTRUMENT_LO
            ? CELL_INSTRUMENT
            : cursorColumn === COL_VOLUME_HI || cursorColumn === COL_VOLUME_LO
              ? CELL_VOLUME
              : CELL_PARAM;

        const cell = store.currentPattern().data;
        const off = (targetRow * song.channels + cursorChannel) * 5 + field;
        const existing = cell[off];
        const isHigh =
          cursorColumn === COL_INSTRUMENT_HI || cursorColumn === COL_VOLUME_HI || cursorColumn === COL_PARAM_HI;
        const value = isHigh ? (digit << 4) | (existing & 0x0f) : (existing & 0xf0) | digit;

        store.writeCell(targetRow, cursorChannel, field, value);
        // Typing the high nibble moves to the low one; the low nibble moves on
        // to the next row, which is how hex entry flows in a tracker.
        if (isHigh) store.moveCursor(0, 1);
        else advance();
        return;
      }

      if (cursorColumn === COL_EFFECT) {
        const effect = effectFromKey(event.code);
        if (effect === null || effect > 35) return;
        event.preventDefault();
        store.writeCell(targetRow, cursorChannel, CELL_EFFECT, effect);
        store.moveCursor(0, 1);
        return;
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      const voice = heldKeys.current.get(event.code);
      if (voice !== undefined) {
        heldKeys.current.delete(event.code);
        audioEngine.noteOff(voice);
      }
    };

    // Releasing focus with keys held would leave notes ringing forever.
    const onBlur = () => {
      for (const voice of heldKeys.current.values()) audioEngine.noteOff(voice);
      heldKeys.current.clear();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [enabled]);
}
